/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import config from '@pages/ActualPolicyPage/config/appCardList.config.json';
import { GET_MY_POLICY_LIST } from '@pages/ActualPolicyPage/apis/graphql';

import { Graphql } from '@common/graphql';
import { apiConfig } from '@/config/apiConfig';
import { vappContainer } from '@/application';
import { AppNavigatorService } from '@/AppNavigator/services';

import { toHeaderCase } from 'js-convert-case';
import moment from 'moment';

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};

export interface IGetCredentialsListProps {
  offset?: number;
  size?: number;
  where?: any;
}

export interface IAppCardListMethods extends IPluginMethodMap {
  getAppCardListProps(args: IGetCredentialsListProps): Promise<any>;
}

export class AppCardListPlugin implements IAgentPlugin {
  readonly methods: IAppCardListMethods;

  constructor() {
    this.methods = {
      getAppCardListProps: this.getProps.bind(this),
    };
  }

  async getProps(args: IGetCredentialsListProps) {
    return await this.getAppCardList(args);
    // return { ...config };
  }

  private async getAppCardList(args: IGetCredentialsListProps) {
    let _where1 = args?.where?.filter(
      (item: any) => item?.column === 'policyNumber'
    );
    let _where2 = args?.where?.filter(
      (item: any) => item?.column !== 'policyNumber'
    );
    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);
      // const token = getToken();
      const token = loginSrv()?.getToken();
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      const res = await client
        .query(GET_MY_POLICY_LIST, {
          input: {
            skip: args?.offset ?? 0,
            limit: args?.size ?? 10,
            where: [..._where1],
            order: [
              {
                column: 'purchasedAt',
                direction: 'DESC',
              },
            ],
            relationFilter: {
              purchasedBy: {
                where: [..._where2],
                limit: args?.size ?? 10,
                skip: args?.offset ?? 0,
              },
            },
          },
        })
        .toPromise();
      if (res?.data?.getMyPolicies) {
        let _templateData = this.formatTemplateList(
          res?.data?.getMyPolicies?.data
        );
        return {
          appCardList: _templateData,
          isEmpty: res?.data?.getMyPolicies?.data?.length <= 0,
          // studentGroup: holderGroups,
          activePage: res?.data?.getMyPolicies?.currentPage,
          pageSize: res?.data?.getMyPolicies?.pageSize,
          totalCount: res?.data?.getMyPolicies?.totalCount,
          totalPages: res?.data?.getMyPolicies?.totalPages,
        };
      }
    } catch (err: any) {
      return {
        isEmpty: true,
      };
    }
  }

  private formatTemplateList(data: any) {
    let _rowData: any =
      Array.isArray(data) &&
      data.map((item: any) => {
        return {
          rowData: item,
          id: item?.policyId,
          // ribbon:{
          //   title:item?.status,
          //   backgroundColor:this.getColor(item?.status)
          // },
          appCardImage: item?.logo ?? config?.icon,
          appCardTitle: item?.productDefination
            ? `Policy Option ${toHeaderCase(item?.productDefination)}`
            : '',
          appCardDescription: item?.createdAt
            ? moment(item?.createdAt).format('DD/MM/YYYY')
            : '',
          appCardList: [
            {
              name: 'Policy No',
              value: item?.policyNumber,
            },
            {
              name: 'Status',
              value: toHeaderCase(item?.status ?? ''),
            },
          ],
        };
      });

    return _rowData;
  }

  private getColor(status: string) {
    switch (status) {
      case 'PAYMENT_PENDING':
        return '#00FFFF';
      case 'ISSUED':
        return '#00ff00';
      case 'EVENT_MONITORING':
        return '#7FFFD4';
      case 'PAYOUT_INTIMATION':
        return '#454B1B';
      case 'PAYOUT_CONFIRMATION':
        return '#7CFC00';
      case 'PAYOUT_ACKNOWLEDGED':
        return '#7FFF00';
      case 'EXPIRED':
        return '#FA8072';
      case 'CANCELED':
        return '#FFA07A';
      default:
        return null;
    }
  }
}
